import React from 'react';
import cn from '../utils/cn';

export type KoobContactCardContact = {
  firstName?: string | null;
  lastName?: string | null;
  job?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  avatarUrl?: string | null;
};

type KoobContactCardProps = {
  title: React.ReactNode;
  /** Shown while no one is filled in. */
  description?: React.ReactNode;
  contact?: KoobContactCardContact | null;
  required?: boolean;
  /** Validation message; turns the card red. */
  error?: React.ReactNode;
  onClick?: () => void;
  'data-testid'?: string;
};

// A button summing up one contact (who, how to reach them) that opens its
// editor. Empty, it shows `description`; invalid, it shows `error` in red, so a
// required contact is never missed behind a closed modal.
const KoobContactCard = React.forwardRef<
  HTMLButtonElement,
  KoobContactCardProps
>(function KoobContactCard(
  {
    title,
    description,
    contact,
    required = false,
    error,
    onClick,
    'data-testid': dataCy,
  },
  ref,
) {
  const name = [contact?.firstName, contact?.lastName]
    .filter(Boolean)
    .join(' ');
  const reach = [contact?.email, contact?.phoneNumber]
    .filter(Boolean)
    .join(' · ');
  const filled = Boolean(name || contact?.email);

  return (
    <div className="flex flex-col gap-1">
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        data-testid={dataCy}
        aria-invalid={Boolean(error)}
        className={cn(
          'flex w-full items-center gap-3 rounded-2xl border bg-white p-4 text-left transition-colors',
          error
            ? 'border-red-500 hover:border-red-600'
            : 'border-k-gray hover:border-navy-200',
        )}
        // no normalize.css: force a solid border (UA default for buttons is outset)
        style={{ borderStyle: 'solid' }}
      >
        {contact?.avatarUrl ? (
          <img
            src={contact.avatarUrl}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              error ? 'bg-red-50 text-red-500' : 'bg-k-orange/10 text-k-orange',
            )}
          >
            <i className="fa-regular fa-user-tie text-lg" />
          </span>
        )}

        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-k-h">
            {title}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </span>
          {filled ? (
            <>
              <span className="truncate text-sm text-k-h">
                {name}
                {contact?.job && (
                  <span className="text-k-p">
                    {name ? ' · ' : ''}
                    {contact.job}
                  </span>
                )}
              </span>
              {reach && (
                <span className="truncate text-xs text-k-p">{reach}</span>
              )}
            </>
          ) : (
            description && (
              <span className="text-sm text-k-p">{description}</span>
            )
          )}
        </span>

        <i
          className={cn(
            'shrink-0 text-base',
            error
              ? 'fa-regular fa-circle-exclamation text-red-500'
              : filled
                ? 'fa-regular fa-pen text-gray-400'
                : 'fa-regular fa-plus text-gray-400',
          )}
        />
      </button>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
});

export default KoobContactCard;
